import { useContext, useRef, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import ScanDocumentModal from "components/ScanDocumentModal";

import { getCompanyPublicKeyResult } from "utils/encryptForCompany";
import dispatchChartService from "api/dispatchChartService";
import { IDispatchChart } from "dto/Mailroom/IDispatchChart";
import { handleCreate } from "utils/uploadFile";
import { ENotificationType, notification } from "utils/notification";

import styles from "./outgoing.module.scss";

interface Props {
  chart: IDispatchChart;
  onClose: () => void;
  onDone: () => void;
}

interface NoteFile {
  uri: string;
  keyId: string | null;
  fileName: string;
}

const ProcessDispatchChartModal = ({ chart, onClose, onDone }: Props) => {
  const { mutate, isLoading } = dispatchChartService.useProcessDispatchChart();

  const [date, setDate] = useState<string>(new Date().toLocaleDateString("sv-SE"));
  const [note, setNote] = useState<NoteFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showScan, setShowScan] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload skanu karty nadawczej — szyfrowany kluczem publicznym profilu (jak w CreateDossierModal).
  const uploadNote = async (file: File) => {
    setUploading(true);
    try {
      // Skan szyfrowany kluczem publicznym FIRMY (model kluczy firmowych) — RZUCA
      // (CompanyKeyMissingError), gdy firma nie ma jeszcze aktywnego klucza.
      const masterKey = await getCompanyPublicKeyResult();

      const r = await handleCreate(
        [file],
        undefined,
        masterKey,
        true,
        true,
        file.name,
        file.type || "application/pdf",
      );

      if (!r || !r[0]) {
        throw new Error("Upload nie zwrócił pliku.");
      }
      setNote({ uri: r[0].uri, keyId: r[0].keyId ?? null, fileName: file.name });
    } catch (e) {
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd",
        text: (e as Error)?.message ?? "Nie udało się dołączyć skanu.",
      });
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadNote(file);
  };

  const submit = () => {
    mutate(
      {
        ChartId: chart.Id,
        DispatchDate: new Date(date).toISOString(),
        DispatchNoteFileUri: note?.uri ?? null,
        DispatchNoteKeyId: note?.keyId ?? null,
      },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Potwierdzono wysyłkę",
            text: `Karta ${chart.Signature} oznaczona jako nadana.`,
          });
          onDone();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się potwierdzić wysyłki karty.",
          }),
      },
    );
  };

  return (
    <>
      <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Potwierdzenie wysyłki karty</div>
        <div className={styles.modalBody}>
          <div className={styles.modalRow}>
            <span>Karta:</span>
            <strong>{chart.Signature}</strong>
          </div>
          <div className={styles.modalRow}>
            <span>Przesyłek:</span>
            <strong>{chart.ParcelCount}</strong>
          </div>
          <div className={styles.field}>
            <label>Data nadania</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label>Skan karty nadawczej (opcjonalnie)</label>
            {note ? (
              <div className={styles.noteRow}>
                <span className={styles.noteName}>{note.fileName}</span>
                <HollowButton text="Usuń" onClick={() => setNote(null)} />
              </div>
            ) : (
              <div className={styles.noteActions}>
                <HollowButton
                  text={uploading ? "Wgrywanie…" : "Wgraj plik"}
                  onClick={() => fileInputRef.current?.click()}
                />
                <HollowButton
                  text="Skanuj"
                  onClick={() => setShowScan(true)}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*"
                  hidden
                  onChange={onPickFile}
                />
              </div>
            )}
          </div>

          <div className={styles.hint}>
            Przesyłki karty zostaną oznaczone jako wysłane (data nadania).
          </div>
        </div>
        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text="Potwierdź wysyłkę"
            onClick={submit}
            loading={isLoading}
            disabled={uploading || !date}
          />
        </div>
      </div>
      </div>

      {showScan && (
        <ScanDocumentModal
          defaultFileName={`karta-${chart.Signature.replace(/\//g, "-")}`}
          handleClose={() => setShowScan(false)}
          onComplete={(pdf) => uploadNote(pdf)}
        />
      )}
    </>
  );
};

export default ProcessDispatchChartModal;
